import { Avatar } from '@/components/ui';
import type { ReactNode } from 'react';
import { profileImageUrl } from '@/hooks/useProfile';
import type { ProjectParticipant } from '@/api/adapter/projectParticipants';
import { useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { ProposalDefinition } from '@/types/project-command';
import type { MilestoneView } from '@/types/current-project';
import type { ProposalPresentation } from '@/api/adapter/proposalReviews';
import {
  deliveryPreferenceLabel,
  proposalCost,
  weekWindowLabel,
} from '@/lib/project-presentation';
import type { DeliveryPreference } from '@/types/delivery-preference';

export function ProposalMilestones({
  definition,
  delivery,
  presentation,
  milestones,
  title = 'Milestones & Cost',
  workers = [],
  milestoneActions,
}: {
  definition: ProposalDefinition;
  delivery?: DeliveryPreference | null;
  presentation?: ProposalPresentation | null;
  milestones?: MilestoneView[];
  title?: string;
  workers?: ProjectParticipant[];
  milestoneActions?: (milestone: MilestoneView) => ReactNode;
}) {
  const { currency } = useDaoConfig();
  const catalog = useSkillsAndRoles();
  const details = presentation?.matchesCurrentDefinition
    ? presentation.milestones
    : [];
  return (
    <>
      {title === 'Milestones' && (
        <h2 className="text-left text-xl font-semibold">Milestones</h2>
      )}
      <section
        className={
          'flex flex-col gap-[--spacing-10] rounded-[--radi-6] border p-[--spacing-10] pb-[--spacing-12] text-left ' +
          (title === 'Milestones' &&
          milestones?.some((m) =>
            ['InProgress', 'CompletionRequested', 'ChangesRequested'].includes(
              m.status ?? ''
            )
          )
            ? 'border-[--state-brand-active] bg-transparent'
            : 'border-[--base-border] bg-transparent')
        }
      >
        {title !== 'Milestones' && (
          <h2 className="text-2xl font-bold">{title}</h2>
        )}
        {presentation && !presentation.matchesCurrentDefinition && (
          <p role="status" className="text-sm text-[--text-dark-secondary]">
            The descriptive details belong to an earlier scope. The current
            milestones are shown below.
          </p>
        )}
        {definition.milestones.map((m, index) => {
          const detail = details.find((d) => d.key === m.key);
          const milestone = milestones?.find(
            (item) => item.definition.key === m.key
          );
          const tasks = milestone?.taskStorage.tasks ?? [];
          return (
            <div key={m.key} className="flex h-fit">
              <div className="flex h-fit w-full gap-[--spacing-8] border-0 bg-transparent p-0">
                <div
                  aria-hidden="true"
                  className="flex flex-col items-center pt-2"
                >
                  <div className="z-10 h-2 w-2 rounded-full bg-transparent outline outline-[5px] outline-offset-1 outline-[--base-fill-3]" />
                  <div
                    className={
                      'h-full w-px ' +
                      (index === definition.milestones.length - 1
                        ? 'bg-transparent'
                        : 'bg-[--base-border]')
                    }
                  />
                </div>
                <div className="flex w-full min-w-0 flex-col gap-[--spacing-4] pb-[--spacing-8]">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <h3 className="text-sm font-medium text-[--text-dark-tertiary]">
                      Milestone {index + 1}
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      <strong>
                        {proposalCost({
                          title: '',
                          description: '',
                          milestones: [m],
                        })}{' '}
                        {currency.symbol}
                      </strong>
                      <span className="text-sm text-[--text-dark-secondary]">
                        {weekWindowLabel(m.window)}
                      </span>
                    </div>
                  </div>
                  <p className="text-lg font-bold">{m.title}</p>
                  {detail?.description && (
                    <p className="max-w-[75ch] whitespace-pre-wrap text-base leading-relaxed text-[--text-dark-secondary]">
                      {detail.description}
                    </p>
                  )}
                  {title === 'Milestones'
                    ? m.requirements.map((r) => (
                        <div
                          key={r.key}
                          className="flex flex-wrap items-center gap-2 text-xs"
                        >
                          <span className="tag">
                            {catalog.getRoleName(r.roleId) ??
                              'Role #' + r.roleId}
                          </span>
                          {r.skillIds.map((id) => (
                            <span key={id} className="tag">
                              {catalog.getSkillName(id) ?? 'Skill #' + id}
                            </span>
                          ))}
                          {detail?.requirements.find(
                            (item) => item.key === r.key
                          )?.experience && (
                            <span className="tag">
                              {
                                detail.requirements.find(
                                  (item) => item.key === r.key
                                )!.experience
                              }
                            </span>
                          )}
                          <span className="text-[--text-dark-secondary]">
                            {r.minutes} minutes · {r.budget} {currency.symbol}
                          </span>
                        </div>
                      ))
                    : m.requirements.map((r) => (
                        <div
                          key={r.key}
                          className="flex items-start gap-3 pt-1"
                        >
                          <span
                            aria-hidden="true"
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[--base-border]"
                          >
                            <i className="ri-user-smile-line text-lg text-[--base-fill-5]" />
                          </span>
                          <div className="min-w-0 text-sm">
                            <p className="font-medium">
                              {catalog.getRoleName(r.roleId) ??
                                'Role #' + r.roleId}{' '}
                              · {r.minutes} minutes · {r.budget} {currency.symbol}
                            </p>
                            <p className="text-[--text-dark-secondary]">
                              {r.skillIds
                                .map(
                                  (id) =>
                                    catalog.getSkillName(id) ?? 'Skill #' + id
                                )
                                .join(', ')}
                            </p>
                            {detail?.requirements.find(
                              (item) => item.key === r.key
                            )?.experience && (
                              <p className="text-[--text-dark-secondary]">
                                Requested experience:{' '}
                                {
                                  detail.requirements.find(
                                    (item) => item.key === r.key
                                  )!.experience
                                }
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                  {title === 'Milestones' && milestone && (
                    <div className="flex flex-wrap items-center gap-4">
                      {[
                        ...new Set(milestone.assignments.map((a) => a.worker)),
                      ].map((id) => {
                        const worker = workers.find((p) => p.accountId === id);
                        const name =
                          worker?.profiles?.worker?.name ??
                          worker?.displayName ??
                          id;
                        return (
                          <div
                            key={id}
                            className="flex items-center gap-2 text-xs text-[--text-dark-secondary]"
                          >
                            <Avatar
                              size="sm"
                              alt={name}
                              src={
                                worker?.workerImage && worker.profiles
                                  ? profileImageUrl(
                                      worker.profiles.principalId,
                                      'worker'
                                    )
                                  : undefined
                              }
                            />
                            <span>{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {title !== 'Milestones' && tasks.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[--text-dark-tertiary]">
                        Tasks / Deliverables
                      </p>
                      <ul className="list-inside list-disc space-y-1 text-sm text-[--text-dark-secondary]">
                        {tasks.map((t) => (
                          <li key={t.taskId}>{t.task.title}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              {title === 'Milestones' && milestone && (
                <div className="text-center">
                  {milestoneActions?.(milestone)}
                </div>
              )}
            </div>
          );
        })}
        {title !== 'Milestones' && (
          <>
            <div className="h-px bg-[--base-border]" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-[--text-dark-secondary]">
                  Total Cost
                </p>
                <strong className="text-2xl">
                  {proposalCost(definition)} {currency.symbol}
                </strong>
              </div>
              <div>
                <p className="text-sm text-[--text-dark-secondary]">
                  Delivery time
                </p>
                <strong>
                  {delivery
                    ? deliveryPreferenceLabel(delivery)
                    : definition.milestones.slice(-1)[0]
                      ? weekWindowLabel(
                          definition.milestones.slice(-1)[0]!.window
                        )
                      : 'Not specified'}
                </strong>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}
